import { Fragment, useEffect, useState } from "react";
import { IApiKey } from "../../interfaces/IApiKey";
import { IIntegracaoHttp, IIntegracaoOperacao, IIntegracaoPagina } from "../../interfaces/IIntegracaoLog";
import ApiKeyService from "../../services/ApiKeyService";
import IntegracaoLogService from "../../services/IntegracaoLogService";
import {
    classeStatusHttp, formatarDataHora, periodoPadrao, ROTULO_SITUACAO_OPERACAO, ROTULO_TIPO_OPERACAO, validarPeriodo
} from "./apiKeyUtils";

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

interface Props {
    // Só para Master; o Administrador sempre vê o próprio cliente.
    clienteId?: number;
}

type Secao = 'operacoes' | 'http';

/**
 * DEVN3-636: aba Logs do menu API Key — operações da API pública (com os erros por item, para o
 * administrador entender por que um lote foi rejeitado) e chamadas HTTP recentes. As entregas de
 * webhook, com o reenvio manual, ficam na aba Webhook.
 */
export default function ApiKeyLogs({ clienteId }: Props) {
    const { t } = useTranslation();

    const [secao, setSecao] = useState<Secao>('operacoes');
    const [chaves, setChaves] = useState<IApiKey[]>([]);
    const [periodo, setPeriodo] = useState(periodoPadrao());
    const [apiKeyId, setApiKeyId] = useState<string>('');
    const [situacao, setSituacao] = useState<string>('');
    const [faixa, setFaixa] = useState<string>('');
    const [pagina, setPagina] = useState<number>(1);
    const [carregando, setCarregando] = useState<boolean>(false);
    const [operacoes, setOperacoes] = useState<IIntegracaoPagina<IIntegracaoOperacao> | null>(null);
    const [http, setHttp] = useState<IIntegracaoPagina<IIntegracaoHttp> | null>(null);
    const [detalhes, setDetalhes] = useState<Record<number, IIntegracaoOperacao | 'carregando'>>({});

    const erroPeriodo = validarPeriodo(periodo.de, periodo.ate);

    useEffect(() => {
        ApiKeyService.listar(clienteId).then(setChaves);
        setApiKeyId('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clienteId]);

    useEffect(() => {
        buscar(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clienteId, secao]);

    async function buscar(novaPagina: number) {
        if (erroPeriodo) return;
        setCarregando(true);
        setPagina(novaPagina);
        const filtro = { clienteId, de: periodo.de, ate: periodo.ate, apiKeyId: apiKeyId ? Number(apiKeyId) : undefined, pagina: novaPagina };
        if (secao === 'operacoes') {
            setDetalhes({});
            setOperacoes(await IntegracaoLogService.operacoes({ ...filtro, situacao: situacao || undefined }));
        } else {
            setHttp(await IntegracaoLogService.http({ ...filtro, faixaStatus: faixa ? Number(faixa) : undefined }));
        }
        setCarregando(false);
    }

    async function alternarDetalhe(operacao: IIntegracaoOperacao) {
        if (detalhes[operacao.id]) {
            const { [operacao.id]: _, ...resto } = detalhes;
            setDetalhes(resto);
            return;
        }
        setDetalhes({ ...detalhes, [operacao.id]: 'carregando' });
        const detalhe = await IntegracaoLogService.operacao(operacao.id, clienteId);
        setDetalhes((atual) => {
            const novo = { ...atual };
            if (detalhe) novo[operacao.id] = detalhe; else delete novo[operacao.id];
            return novo;
        });
    }

    const nomeChave = (id?: number | null, nome?: string | null, prefixo?: string | null) => {
        if (nome) return `${nome} (${prefixo}…)`;
        const chave = chaves.find((c) => c.id === id);
        return chave ? `${chave.nome} (${chave.prefixo}…)` : '—';
    };

    const paginaAtual = secao === 'operacoes' ? operacoes : http;
    const totalPaginas = paginaAtual ? Math.max(1, Math.ceil(paginaAtual.total / paginaAtual.tamanhoPagina)) : 1;

    return <>
        <p className="text-muted">
            {t('Acompanhe as integrações pela API pública: o resultado de cada lote enviado (com os erros por item quando ele é rejeitado) e as chamadas recebidas. As entregas do webhook ficam na aba Webhook.')}
        </p>

        <div className="btn-group mb-4" role="group">
            <button type="button" className={`btn btn-sm ${secao === 'operacoes' ? 'btn-primario' : 'btn-light'}`} onClick={() => setSecao('operacoes')}>
                {t('Operações')}
            </button>
            <button type="button" className={`btn btn-sm ${secao === 'http' ? 'btn-primario' : 'btn-light'}`} onClick={() => setSecao('http')}>
                {t('Chamadas HTTP')}
            </button>
        </div>

        <div className="d-flex flex-wrap align-items-end gap-3 mb-4">
            <div>
                <label htmlFor="logs-de" className="form-label mb-1">{t('De')}</label>
                <input id="logs-de" type="date" className={`form-control form-control-sm ${erroPeriodo ? 'is-invalid' : ''}`} value={periodo.de}
                    onChange={(e) => setPeriodo({ ...periodo, de: e.target.value })} />
            </div>
            <div>
                <label htmlFor="logs-ate" className="form-label mb-1">{t('Até')}</label>
                <input id="logs-ate" type="date" className={`form-control form-control-sm ${erroPeriodo ? 'is-invalid' : ''}`} value={periodo.ate}
                    onChange={(e) => setPeriodo({ ...periodo, ate: e.target.value })} />
            </div>
            <div>
                <label htmlFor="logs-chave" className="form-label mb-1">{t('Chave')}</label>
                <select id="logs-chave" className="form-select form-select-sm" value={apiKeyId} onChange={(e) => setApiKeyId(e.target.value)}>
                    <option value="">{t('Todas')}</option>
                    {chaves.map((c) => <option key={c.id} value={c.id}>{c.nome} ({c.prefixo}…)</option>)}
                </select>
            </div>
            {secao === 'operacoes'
                ? <div>
                    <label htmlFor="logs-situacao" className="form-label mb-1">{t('Situação')}</label>
                    <select id="logs-situacao" className="form-select form-select-sm" value={situacao} onChange={(e) => setSituacao(e.target.value)}>
                        <option value="">{t('Todas')}</option>
                        {Object.entries(ROTULO_SITUACAO_OPERACAO).map(([valor, r]) => <option key={valor} value={valor}>{t(r.texto)}</option>)}
                    </select>
                </div>
                : <div>
                    <label htmlFor="logs-status" className="form-label mb-1">{t('Status')}</label>
                    <select id="logs-status" className="form-select form-select-sm" value={faixa} onChange={(e) => setFaixa(e.target.value)}>
                        <option value="">{t('Todos')}</option>
                        <option value="2">2xx</option>
                        <option value="4">4xx</option>
                        <option value="5">5xx</option>
                    </select>
                </div>}
            <button type="button" className="btn btn-sm btn-primario" disabled={!!erroPeriodo || carregando} onClick={() => buscar(1)}>
                <i className="fas fa-search text-white"></i> {t('Filtrar')}
            </button>
        </div>
        {erroPeriodo && <div className="text-danger mb-3">{t(erroPeriodo)}</div>}

        {carregando ? <p>{t('Carregando...')}</p>
            : secao === 'operacoes'
                ? <TabelaOperacoes />
                : <TabelaHttp />}

        {paginaAtual && paginaAtual.total > 0 && !carregando &&
            <div className="d-flex align-items-center justify-content-end gap-3 mt-3">
                <span className="text-muted">{t('Página')} {pagina} / {totalPaginas} — {paginaAtual.total} {t('registro(s)')}</span>
                <button type="button" className="btn btn-sm btn-light" disabled={pagina <= 1} onClick={() => buscar(pagina - 1)}>{t('Anterior')}</button>
                <button type="button" className="btn btn-sm btn-light" disabled={pagina >= totalPaginas} onClick={() => buscar(pagina + 1)}>{t('Próxima')}</button>
            </div>}
    </>;

    function TabelaOperacoes() {
        if (!operacoes || operacoes.itens.length === 0) return <p className="text-muted">{t('Nenhuma operação no período.')}</p>;
        return <div className="table-responsive">
            <table className="table table-row-dashed align-middle gs-0 gy-3">
                <thead>
                    <tr className="fw-bolder text-muted">
                        <th></th>
                        <th>{t('Data')}</th>
                        <th>{t('Operação')}</th>
                        <th>{t('Tipo')}</th>
                        <th>{t('Importação')}</th>
                        <th>{t('Chave')}</th>
                        <th>{t('Situação')}</th>
                        <th className="text-end">{t('Itens')}</th>
                        <th className="text-end">{t('Gravados')}</th>
                        <th className="text-end">{t('Erros')}</th>
                    </tr>
                </thead>
                <tbody>
                    {operacoes.itens.map((op) => {
                        const rotulo = ROTULO_SITUACAO_OPERACAO[op.situacao] ?? { texto: op.situacao, classe: 'badge-light' };
                        const detalhe = detalhes[op.id];
                        const temDetalhe = op.situacao === 'failed' || op.totalErros > 0 || !!op.motivoFalha;
                        return <Fragment key={op.id}>
                            <tr>
                                <td>
                                    {temDetalhe &&
                                        <button type="button" className="btn btn-sm btn-icon btn-light" onClick={() => alternarDetalhe(op)} aria-label={t('Ver erros')}>
                                            <i className={`fas ${detalhe ? 'fa-chevron-down' : 'fa-chevron-right'}`}></i>
                                        </button>}
                                </td>
                                <td>{formatarDataHora(op.criadaEm)}</td>
                                <td>{op.id}</td>
                                <td>{t(ROTULO_TIPO_OPERACAO[op.tipo] ?? op.tipo)}</td>
                                <td>{op.importacaoId}</td>
                                <td>{nomeChave(op.apiKeyId, op.apiKeyNome, op.apiKeyPrefixo)}</td>
                                <td><span className={`badge ${rotulo.classe}`}>{t(rotulo.texto)}</span></td>
                                <td className="text-end">{op.totalItens.toLocaleString('pt-BR')}</td>
                                <td className="text-end">{op.itensGravados.toLocaleString('pt-BR')}</td>
                                <td className="text-end">{op.totalErros.toLocaleString('pt-BR')}</td>
                            </tr>
                            {detalhe &&
                                <tr className="bg-light">
                                    <td></td>
                                    <td colSpan={9}>
                                        {detalhe === 'carregando' ? t('Carregando...') : <>
                                            {detalhe.motivoFalha && <div className="fw-bold mb-2">{t(detalhe.motivoFalha)}</div>}
                                            {detalhe.erros.length > 0 && <>
                                                <table className="table table-sm mb-1">
                                                    <thead>
                                                        <tr className="text-muted">
                                                            <th>{t('Item')}</th>
                                                            <th>{t('Campo')}</th>
                                                            <th>{t('Código')}</th>
                                                            <th>{t('Mensagem')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {detalhe.erros.map((e, i) => (
                                                            <tr key={i}>
                                                                <td>{e.indice}</td>
                                                                <td><code>{e.campo ?? '—'}</code></td>
                                                                <td><code>{e.codigo}</code></td>
                                                                <td>{e.mensagem}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                                <small className="text-muted">
                                                    {t('O item é a posição no lote enviado, a partir de 0.')}
                                                    {detalhe.totalErros > detalhe.erros.length &&
                                                        <> {t('Mostrando')} {detalhe.erros.length} {t('de')} {detalhe.totalErros} {t('erros')}.</>}
                                                </small>
                                            </>}
                                        </>}
                                    </td>
                                </tr>}
                        </Fragment>;
                    })}
                </tbody>
            </table>
        </div>;
    }

    function TabelaHttp() {
        if (!http || http.itens.length === 0) return <p className="text-muted">{t('Nenhuma chamada no período.')}</p>;
        return <div className="table-responsive">
            <table className="table table-row-dashed align-middle gs-0 gy-3">
                <thead>
                    <tr className="fw-bolder text-muted">
                        <th>{t('Data')}</th>
                        <th>{t('Chave')}</th>
                        <th>{t('Método')}</th>
                        <th>{t('Rota')}</th>
                        <th>{t('Status')}</th>
                        <th className="text-end">{t('Duração')}</th>
                        <th>{t('IP')}</th>
                        <th>{t('Origem')}</th>
                        <th>{t('Trace')}</th>
                    </tr>
                </thead>
                <tbody>
                    {http.itens.map((l) => (
                        <tr key={l.id}>
                            <td className="text-nowrap">{formatarDataHora(l.criadoEm)}</td>
                            <td>{nomeChave(l.apiKeyId)}</td>
                            <td><code>{l.metodo}</code></td>
                            <td className="text-break"><code>{l.rota}</code></td>
                            <td><span className={`badge ${classeStatusHttp(l.status)}`}>{l.status}</span></td>
                            <td className="text-end">{l.duracaoMs} ms</td>
                            <td>{l.ip ?? '—'}</td>
                            <td className="text-break">{l.origem ?? '—'}</td>
                            <td><small className="text-muted text-break">{l.traceId ?? '—'}</small></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>;
    }
}
